import React, { useState } from 'react';
import { Search, X, Link2, GitBranch, Clock } from 'lucide-react';
import { Task } from '../../lib/db';

interface TaskDependencySelectorProps {
  currentTaskId?: string;
  selectedDependencies: string[];
  availableTasks: Task[];
  dependencyType: 'none' | 'sequential' | 'parallel';
  onDependenciesChange: (dependencies: string[]) => void;
  onDependencyTypeChange: (type: 'none' | 'sequential' | 'parallel') => void;
}

function TaskDependencySelector({
  currentTaskId,
  selectedDependencies,
  availableTasks,
  dependencyType,
  onDependenciesChange,
  onDependencyTypeChange
}: TaskDependencySelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Filter out current task and already selected tasks
  const filteredTasks = availableTasks.filter(task => 
    task.id !== currentTaskId &&
    !selectedDependencies.includes(task.id) &&
    task.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTasks = availableTasks.filter(task => 
    selectedDependencies.includes(task.id)
  );

  const addDependency = (taskId: string) => {
    onDependenciesChange([...selectedDependencies, taskId]);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const removeDependency = (taskId: string) => {
    onDependenciesChange(selectedDependencies.filter(id => id !== taskId));
  };

  const getDependencyTypeIcon = () => {
    switch (dependencyType) {
      case 'sequential':
        return <GitBranch className="w-4 h-4" />;
      case 'parallel':
        return <Clock className="w-4 h-4" />;
      default:
        return <Link2 className="w-4 h-4" />;
    }
  };

  const getDependencyTypeDescription = () => {
    switch (dependencyType) {
      case 'sequential':
        return 'All dependencies must complete before this task becomes live';
      case 'parallel':
        return 'This task can be worked on alongside its dependencies';
      default:
        return 'This task has no dependencies';
    }
  };

  return (
    <div className="space-y-4">
      {/* Dependency Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dependency Type
        </label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => onDependencyTypeChange('none')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              dependencyType === 'none'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            None
          </button>
          <button
            type="button"
            onClick={() => onDependencyTypeChange('sequential')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              dependencyType === 'sequential'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Sequential
          </button>
          <button
            type="button"
            onClick={() => onDependencyTypeChange('parallel')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              dependencyType === 'parallel'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Parallel
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500 flex items-center gap-2">
          {getDependencyTypeIcon()}
          {getDependencyTypeDescription()}
        </p>
      </div>

      {/* Dependencies Selector */}
      {dependencyType !== 'none' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Task Dependencies
          </label>
          
          {/* Selected Dependencies */}
          {selectedTasks.length > 0 && (
            <div className="mb-3 space-y-2">
              {selectedTasks.map(task => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{task.title}</span>
                    {task.status === 'Complete' && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        Complete
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDependency(task.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search for tasks to depend on..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Dropdown */}
            {showDropdown && (searchTerm || filteredTasks.length > 0) && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredTasks.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No tasks found
                  </div>
                ) : (
                  filteredTasks.map(task => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => addDependency(task.id)}
                      className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-medium text-sm">{task.title}</div>
                      {task.description && (
                        <div className="text-xs text-gray-500 mt-1">
                          {task.description}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          task.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                          task.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {task.priority}
                        </span>
                        {task.status && (
                          <span className="text-xs text-gray-500">
                            {task.status}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Click outside to close dropdown */}
          {showDropdown && (
            <div
              className="fixed inset-0 z-0"
              onClick={() => setShowDropdown(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default TaskDependencySelector;